'use client';

import { useEffect } from 'react';
import { usePathname, useRouter, useParams } from 'next/navigation';

let _pendingAnchor: string | null = null;

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Used after cross-page navigation — element may not be in DOM yet, so retry
function scrollToIdWithRetry(id: string, homePath: string) {
  const startTime = Date.now();
  const tryScroll = () => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({
        behavior: reducedMotion() ? 'instant' : 'smooth',
        block: 'start',
      });
      history.replaceState(null, '', `${homePath}#${id}`);
      return;
    }
    if (Date.now() - startTime < 5000) setTimeout(tryScroll, 50);
  };
  requestAnimationFrame(tryScroll);
}

// Used on same page — element is already in DOM, scroll directly
function scrollToId(id: string, homePath: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({
    behavior: reducedMotion() ? 'instant' : 'smooth',
    block: 'start',
  });
  history.replaceState(null, '', `${homePath}#${id}`);
}

export function useAnchorNav() {
  const pathname = usePathname();
  const router = useRouter();
  const params = useParams();
  const locale = (params?.locale as string) ?? 'pt';
  const homePath = `/${locale}`;
  const onHome = pathname === homePath;

  useEffect(() => {
    if (!onHome || !_pendingAnchor) return;
    const id = _pendingAnchor;
    _pendingAnchor = null;
    scrollToIdWithRetry(id, homePath);
  }, [onHome, homePath]);

  const handleAnchorClick = (e: React.MouseEvent, href: string) => {
    if (!href.includes('#')) return;

    const id = href.split('#')[1];
    if (!id) return;

    e.preventDefault();

    if (!onHome) {
      _pendingAnchor = id;
      router.push(homePath, { scroll: false });
      return;
    }

    scrollToId(id, homePath);
  };

  return { handleAnchorClick };
}
